import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StyleObj } from '../../../../@types';
import { BETSLIP_WIDTH } from '../../../../constants';
import { useBetslipContext } from '../../../../contexts/BetslipContext';
import {
  useBetslipDrawerState,
  useBetslipDisabledState,
  usePlaceBet,
  usePendingBetslipTracker,
  useBetslipCalculations,
  useBetslipPayload,
} from '../../../../hooks/useBetslip';
import { getCombinations } from 'sportsbook-shared-module';
import { useGroupedBetslipItems } from '../hooks/useGroupedBets';
import BetslipInfoMessage from '../../../atoms/BetslipInfoMessage';
import SignInButton from '../../../atoms/SignInButton';
import UserSelect from '../../../molecules/UserSelect';
import FullScreenLoader from '../../../atoms/FullScreenLoader';
import NumericKeyboard from '../atoms/NumericKeyboard';
import BetslipStakeInput from '../molecules/BetslipStakeInput';
import BetBuilderBetslipItem from '../molecules/BetBuilderBetslipItem';
import BetslipItem from '../molecules/BetslipItem';
import BetslipPlaceBetButton from '../molecules/BetslipPlaceBetButton';
import BetslipHeader from '../molecules/BetslipHeader';
import { useRoles } from '../../../../hooks/useRoles';
import { useBetslipBonus } from '../../../../hooks/useBetslipBonus';
import useLocalization from '../../../../hooks/useLocalization';
import { BonusInfoMessage } from '../atoms';

const styles: StyleObj = {
  drawer: {
    maxHeight: '80dvh',
    textAlign: 'center',
    '& .MuiPaper-root': {
      width: {
        xs: '100%',
        sm: BETSLIP_WIDTH,
      },
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '80dvh',
      background: (theme) => theme.palette.neutral[50],
      borderRadius: {
        xs: 0,
        sm: '4px 4px 0 0',
      },
      margin: 'auto',
      mb: {
        xs: 0,
        md: 4,
      },
    },
    '& form': {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      overflow: 'hidden',
      minHeight: 0,
    },
  },
  betStack: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
      width: 14,
      borderColor: 'neutral.50',
    },
    '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
      backgroundColor: 'neutral.300',
    },
  },
  removeAllButton: { minHeight: 0, p: 0, pl: 1.5, ':hover': { background: 'none' } },
  signInButton: {
    backgroundColor: 'primary.main',
    borderRadius: 0,
    color: 'inherit',
    typography: 'h5',
    '&:hover': { backgroundColor: 'primary.700' },
    minHeight: 44,
    border: 'none',
    width: '100%',
    flexShrink: 0,
  },
  inputRow: {
    flexShrink: 0,
  },
};

const GolaBetslipDrawer = () => {
  const [stakeValue, setStakeValue] = useState('');
  const [showKeyboard, setShowKeyboard] = useState(false);
  const stakeInputRef = useRef<HTMLInputElement | null>(null);

  const { isAgent } = useRoles();

  const { open, setOpen, selectedPlayer, setSelectedPlayer, closeBetslipDrawer } = useBetslipDrawerState();

  const { bets, removeAllBets, infoMessages, selectedWallet, setSelectedWallet, reofferedBettingSlipsIds } =
    useBetslipContext();

  const handleWalletChange = useCallback(
    (value: string) => setSelectedWallet(value as typeof selectedWallet),
    [setSelectedWallet]
  );

  const groupedBets = useGroupedBetslipItems();

  const preparedBetsForCombinations = useMemo(
    () =>
      bets.map((bet) => ({
        ...bet,
        marketTypeId: bet.marketType.id,
        marketTypeCombiningIds: bet.marketType.marketTypeCombiningIds ?? [],
        singlesStakeAmount: 0,
      })),
    [bets]
  );

  const stakeAmounts = useMemo(() => {
    const combinations = getCombinations(preparedBetsForCombinations, true);
    const [bettingType, combinationCount] = Object.entries(combinations)[0] ?? ['Single', 1];

    const numericStake = parseFloat(stakeValue) || 0;
    const stakePerCombination = combinationCount > 0 ? numericStake / combinationCount : numericStake;

    return { [bettingType]: stakePerCombination.toString() };
  }, [preparedBetsForCombinations, stakeValue]);

  const { betTypes, totalStakeAmount, totalOdds, maxPayout, minWinningAmount, minOdds, maxOdds } =
    useBetslipCalculations(stakeAmounts);

  const hasMinWinningAmount = typeof minWinningAmount === 'number';

  const { t } = useLocalization();

  const { bonusPercentage, totalWinningAmount, showAddMoreSelectionsMsg, showBonusNotAvailableMsg, eligibility } =
    useBetslipBonus({ maxPayout, isSystemTicket: false, selectedWallet });

  const discardBetslip = useCallback(() => {
    closeBetslipDrawer();
    removeAllBets();
    setSelectedPlayer(null);
    setStakeValue('');
  }, [closeBetslipDrawer, removeAllBets, setSelectedPlayer]);

  const { hasPendingBetslips } = usePendingBetslipTracker({
    onAllBetslipsResolved: discardBetslip,
  });

  const { placeBetMutation } = usePlaceBet({
    discardBetslip,
  });

  const { hasDisabledBets, disabledByRole, isAuthenticated } = useBetslipDisabledState({
    totalStakeAmount,
    isAgent,
    selectedPlayerId: selectedPlayer?.id,
    hasPendingBetslips,
  });

  const isStakeMissing = !stakeValue;
  const isPlaceBetButtonDisabled =
    hasDisabledBets || disabledByRole || placeBetMutation.isPending || (!isStakeMissing && !totalStakeAmount);
  const hasReofferedBetslips = reofferedBettingSlipsIds.length > 0;
  const hasBets = bets.length > 0;

  const { preparePayload } = useBetslipPayload({
    betTypes,
    totalStakeAmount,
    isAgent,
    selectedPlayerId: selectedPlayer?.id,
  });

  const placeBet = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const payload = preparePayload();
      placeBetMutation.mutate(payload);
    },
    [preparePayload, placeBetMutation]
  );

  const handleKeyboardChange = useCallback((newValue: string) => {
    setStakeValue(newValue);
  }, []);

  const handleKeyboardClose = useCallback(() => {
    setShowKeyboard(false);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setShowKeyboard(false);
    closeBetslipDrawer();
  }, [closeBetslipDrawer]);

  const handleStakeInputToggle = useCallback(() => {
    setShowKeyboard((prev) => !prev);
  }, []);

  const focusStakeInput = useCallback(() => {
    stakeInputRef.current?.focus();
  }, []);

  const handlePlaceBetButtonClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (!isStakeMissing) {
        return;
      }

      event.preventDefault();
      setShowKeyboard(true);
      focusStakeInput();
    },
    [focusStakeInput, isStakeMissing]
  );

  useEffect(() => {
    if (bets.length === 0) {
      closeBetslipDrawer();
      setStakeValue('');
    }
  }, [bets.length, closeBetslipDrawer]);

  return (
    <>
      <FullScreenLoader open={hasPendingBetslips} />
      {hasBets && !open && (
        <BetslipHeader
          numberOfBets={bets.length}
          totalOdds={totalOdds}
          minOdds={minOdds}
          maxOdds={maxOdds}
          selectedWallet={selectedWallet}
          onClose={discardBetslip}
          onClick={() => setOpen(true)}
          isCollapsed
          onWalletChange={handleWalletChange}
        />
      )}
      <Drawer
        anchor='bottom'
        elevation={0}
        open={open && hasBets && !hasReofferedBetslips}
        onClose={handleDrawerClose}
        sx={styles.drawer}
      >
        <form onSubmit={placeBet}>
          {Array.from(infoMessages)?.map((message) => <BetslipInfoMessage key={message} message={message} />)}
          <BetslipHeader
            numberOfBets={bets.length}
            totalOdds={totalOdds}
            minOdds={minOdds}
            maxOdds={maxOdds}
            selectedWallet={selectedWallet}
            onClose={discardBetslip}
            onClick={() => setOpen(false)}
            onWalletChange={handleWalletChange}
          />

          {isAgent && (
            <Stack>
              <UserSelect selectedUser={selectedPlayer} onUserSelect={setSelectedPlayer} />
            </Stack>
          )}

          <Stack sx={styles.betStack}>
            {groupedBets.map((row, index) =>
              row.kind === 'betBuilder' ? (
                <BetBuilderBetslipItem key={row.bet.outcomeId} bet={row.bet} onRemoveBet={() => undefined} />
              ) : (
                <BetslipItem key={`group-${index}`} bets={row.bets} onRemoveBet={() => undefined} />
              )
            )}
          </Stack>

          {showAddMoreSelectionsMsg && (
            <BonusInfoMessage>
              {t('bonusInfo.addMoreSelections', {
                count: eligibility.selectionsNeeded,
                odd: eligibility.minRequiredOdd?.toFixed(2),
                percentage: eligibility.nextBonusPercentage,
              })}
            </BonusInfoMessage>
          )}
          {showBonusNotAvailableMsg && <BonusInfoMessage>{t('bonusInfo.bonusNotAvailable')}</BonusInfoMessage>}

          {!isAuthenticated ? (
            <SignInButton sx={styles.signInButton} />
          ) : (
            <Stack direction='row' sx={styles.inputRow}>
              <BetslipStakeInput
                value={stakeValue}
                onToggle={handleStakeInputToggle}
                showKeyboard={showKeyboard}
                showArrow={showKeyboard}
                inputRef={stakeInputRef}
              />
              <BetslipPlaceBetButton
                totalStakeAmount={totalStakeAmount}
                maxPayout={totalWinningAmount}
                isDisabled={isPlaceBetButtonDisabled}
                isLoading={placeBetMutation.isPending}
                bonusPercentage={bonusPercentage}
                minWinningAmount={hasMinWinningAmount ? minWinningAmount : null}
                onClick={handlePlaceBetButtonClick}
                onPointerDown={handlePlaceBetButtonClick}
              />
            </Stack>
          )}
        </form>
        {showKeyboard && (
          <NumericKeyboard value={stakeValue} onChange={handleKeyboardChange} onClose={handleKeyboardClose} />
        )}
      </Drawer>
    </>
  );
};

export default GolaBetslipDrawer;
